import { rapidReady } from '../../../../lib/utils/rapid'
import { ArticleContainer } from './article_container'
import { dispatchCustomEvent } from '../../../../lib/utils/events'
import { addRecentlyViewedItem } from '../../../../lib/utils/recently_viewed'

export default (() => {
  class RelatedArticles {
    constructor({ selector }) {
      this.containerElems = [...document.querySelectorAll(selector)]

      if (!this.containerElems.length) {
        return
      }

      this.originalPath = window.location.pathname
      this.originalSearch = window.location.search
      this.originalTitle = document.title
      this.originalPublisher = window.__GAM_CONFIG?.i13n?.pcp
      this.originalLmsid = window.__GAM_CONFIG?.i13n?.lmsid
      this.originalUuid = rapidConfig?.keys?.pstaid
      this.initialArticle = this.getInitialArticle()
      this.currentArticle = this.initialArticle
      window.wafer.ready(() => {
        this.init()
        this.addWaferEvent()
      })
      this.lastKnownReferrer = window.location.href
    }

    getInitialArticle() {
      let initialArticle
      const article =
        document.querySelector('.content-canvas') || document.querySelector('.m-article') // CaaS article // News Balance article

      if (article) {
        initialArticle = new ArticleContainer(
          article.parentElement,
          article,
          {
            uuid: this.originalUuid,
            title: this.originalTitle,
            href: this.originalPath,
            publisher: this.originalPublisher,
            lmsid: this.originalLmsid
          },
          { preLoaded: true }
        )
      }
      return initialArticle
    }

    init() {
      const { debounce } = wafer.utils
      this.articleContainers = this.getArticleContainers()
      if (!this.articleContainers.length) {
        return
      }
      this.debouncedScrollHandler = debounce(this.handleScroll.bind(this), 200)
      window.addEventListener('scroll', this.debouncedScrollHandler)
      window.wafer.on('caas:article:fetch', this.handleArticleFetch.bind(this))
    }

    addWaferEvent() {
      window.wafer.on('fetch:success', event => {
        if (event?.elem?.classList.contains('m-article-container--supplemental')) {
          dispatchCustomEvent('supplementalcontent:added', event)
        }
      })
    }

    handleArticleFetch({ elem, meta: { data = {} } = {} }) {
      const articleContainer = this.articleContainers.find(
        container => container.articleElem === elem
      )

      if (articleContainer) {
        articleContainer.moveAd()
        articleContainer.setCaasData(data)
      }
    }

    handleScroll() {
      // Logic to get article most in view and set active
      const mostInView = this.articleContainers.reduce((current, next) => {
        if (next.isLoaded && next.inViewPercentage > current.inViewPercentage) {
          return next
        }
        return current
      }, this.currentArticle)

      if (mostInView && mostInView !== this.currentArticle) {
        const {
          data: { uuid }
        } = mostInView
        this.currentArticle = mostInView
        this.updatePageUrl(mostInView)
        this.fireTrackingBeacons(mostInView)
        this.updateBenjiTracking(mostInView)
        this.lastKnownReferrer = window.location.href
        addRecentlyViewedItem('article', uuid)
      }
    }

    fireTrackingBeacons(article) {
      this.rapidPageView(article)
      this.comscoreBeacon()
    }

    rapidPageView(article) {
      const {
        data: { uuid },
        publisher
      } = article
      const pCpos = this.articleContainers.length - this.articleContainers.indexOf(article)
      rapidReady(rapid => {
        const keys = rapid.getRapidAttribute('keys') || []
        const spaceid = rapid.getRapidAttribute('spaceid') || rapidConfig.spaceid
        const rapidKeys = {
          ...keys,
          ...{
            p_cpos: pCpos,
            ...(pCpos > 1 && { expn: 'perpetual-post' }),
            pl2: 'seamless-article',
            pcp: publisher,
            pt: 'content',
            pct: 'story',
            pstaid: `${uuid}`,
            spaceid
          }
        }
        rapid.beaconRouteChange({
          location: window.location.href,
          referrer: this.lastKnownReferrer,
          keys: rapidKeys
        })
      })
    }

    comscoreBeacon() {
      const comscoreConfig = { c1: '2', c2: '1000009' }
      if (window.COMSCORE) {
        window.COMSCORE.beacon(comscoreConfig)
      } else {
        if (!window._comscore) {
          window._comscore = []
        }
        const { _comscore } = window
        _comscore.push(comscoreConfig)
        const s = document.createElement('script')
        const el = document.getElementsByTagName('script')[0]
        s.async = true
        s.src = 'https://s.yimg.com/cx/vzm/cs.js'
        el.parentNode.insertBefore(s, el)
      }
    }

    updatePageUrl(article) {
      const { data: { title, href } = {} } = article
      document.title = title
      const { history } = window
      history.replaceState(
        {
          state: history.state
        },
        '',
        `${href}${this.originalSearch}`
      )
    }

    updateBenjiTracking(article) {
      rapidReady(rapid => {
        const keys = rapid.getRapidAttribute('keys') || []
        const spaceid = rapid.getRapidAttribute('spaceid') || rapidConfig.spaceid
        const metaData = {
          ...keys,
          spaceid,
          url: window.location.href,
          lmsid: article.lmsid
        }
        window.benji?.updateI13N?.(metaData)
      })
    }

    getArticleContainers() {
      const containers = this.containerElems
        .map(elem => new ArticleContainer(elem))
        .filter(container => container.validate())
        .reverse()

      // This is constructing an object for the original article which was not lazy loaded
      if (this.initialArticle) {
        containers.push(this.initialArticle)
      }

      return containers
    }
  }

  return RelatedArticles
})()
